import { Example, NewsPost, Page } from '../types/content';
import matter from 'gray-matter';
import { Buffer } from 'buffer';

// Polyfill Buffer for browser environment
if (typeof window !== 'undefined' && !(window as any).Buffer) {
  (window as any).Buffer = Buffer;
  (globalThis as any).Buffer = Buffer;
}

// Content manifest interface
interface ContentManifest {
  news: string[];
  examples: string[];
  pages: string[];
  lastUpdated: string;
}

// Cache for the manifest
let manifestCache: ContentManifest | null = null;
let manifestLastFetch = 0;
const MANIFEST_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Cache for content
let newsPostsCache: NewsPost[] | null = null;
let newsPostsLastFetch = 0;
let examplesCache: Example[] | null = null;
let examplesLastFetch = 0;
const CONTENT_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

/**
 * Load content manifest dynamically
 */
export async function loadManifest(): Promise<ContentManifest> {
  const now = Date.now();
  
  // Return cached manifest if still fresh
  if (manifestCache && (now - manifestLastFetch) < MANIFEST_CACHE_TTL) {
    return manifestCache;
  }
  
  try {
    console.log('Loading content manifest...');
    const response = await fetch('/content/manifest.json');
    if (!response.ok) {
      throw new Error(`Failed to load manifest: ${response.status}`);
    }
    
    manifestCache = await response.json();
    manifestLastFetch = now;
    console.log('Manifest loaded:', {
      news: manifestCache!.news.length,
      examples: manifestCache!.examples.length,
      pages: manifestCache!.pages.length
    });
    
    return manifestCache!;
  } catch (error) {
    console.error('Error loading manifest:', error);
    throw error;
  }
}

console.log('Content loader initialized with dynamic manifest approach');

/**
 * Load all news posts from content directory
 */
export async function loadNewsPosts(): Promise<NewsPost[]> {
  const now = Date.now();
  
  // Return cached posts if still fresh
  if (newsPostsCache && (now - newsPostsLastFetch) < CONTENT_CACHE_TTL) {
    console.log('Returning cached news posts');
    return newsPostsCache;
  }
  
  try {
    console.log('Loading news posts via HTTP fetch...');
    const manifest = await loadManifest();
    
    // Load all files in parallel for much better performance
    const loadPromises = manifest.news.map(async (filePath) => {
      try {
        console.log(`Fetching: ${filePath}`);
        const response = await fetch(filePath);
        
        if (!response.ok) {
          console.error(`Failed to fetch ${filePath}:`, response.status, response.statusText);
          return null;
        }
        
        const markdown = await response.text();
        const { data: frontmatter, content } = matter(markdown);
        
        // Extract slug from file path
        const slug = filePath.split('/').pop()?.replace('.md', '') || '';
        
        const post: NewsPost = {
          slug,
          title: frontmatter.title || '',
          dek: frontmatter.dek || '',
          source_name: frontmatter.source_name || '',
          source_url: frontmatter.source_url || '',
          published_at: frontmatter.published_at || new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
          topics: frontmatter.topics || [],
          curator_note: frontmatter.curator_note || '',
          featured: frontmatter.featured || false,
          content
        };
        
        console.log('Loaded post:', post.title);
        return post;
      } catch (error) {
        console.error(`Error loading news post from ${filePath}:`, error);
        return null;
      }
    });
    
    // Wait for all files to load in parallel
    const results = await Promise.all(loadPromises);
    
    // Filter out failed loads and sort by date
    const posts = results.filter((post): post is NewsPost => post !== null);
    console.log(`Successfully loaded ${posts.length} news posts`);
    
    // Sort by published date (newest first)
    const sortedPosts = posts.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());
    
    // Cache the results
    newsPostsCache = sortedPosts;
    newsPostsLastFetch = now;
    
    return sortedPosts;
  } catch (error) {
    console.error('Error loading news posts:', error);
    throw error;
  }
}

/**
 * Load a single news post by slug or date-based path
 */
export async function loadNewsPost(slug: string, year?: string, month?: string): Promise<NewsPost | null> {
  // If year/month provided, load directly from file path
  if (year && month) {
    try {
      const filePath = `/content/news/${year}/${month}/${slug}.md`;
      console.log(`Loading post directly from: ${filePath}`);
      
      const response = await fetch(filePath);
      if (!response.ok) {
        console.error(`Failed to fetch ${filePath}:`, response.status);
        return null;
      }
      
      const markdown = await response.text();
      const { data: frontmatter, content } = matter(markdown);
      
      const post: NewsPost = {
        slug,
        title: frontmatter.title || '',
        dek: frontmatter.dek || '',
        source_name: frontmatter.source_name || '',
        source_url: frontmatter.source_url || '',
        published_at: frontmatter.published_at || new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
        topics: frontmatter.topics || [],
        curator_note: frontmatter.curator_note || '',
        featured: frontmatter.featured || false,
        content
      };
      
      return post;
    } catch (error) {
      console.error(`Error loading post ${year}/${month}/${slug}:`, error);
      return null;
    }
  }
  
  // Fallback: search through all posts for legacy slug-only URLs
  const posts = await loadNewsPosts();
  return posts.find(post => post.slug === slug) || null;
}

/**
 * Load all examples from content directory
 */
export async function loadExamples(): Promise<Example[]> {
  const now = Date.now();
  
  // Return cached examples if still fresh
  if (examplesCache && (now - examplesLastFetch) < CONTENT_CACHE_TTL) {
    console.log('Returning cached examples');
    return examplesCache;
  }
  
  try {
    console.log('Loading examples via HTTP fetch...');
    const manifest = await loadManifest();
    
    // Load all files in parallel for better performance
    const loadPromises = manifest.examples.map(async (filePath) => {
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          console.error(`Failed to fetch ${filePath}:`, response.status);
          return null;
        }
        
        const markdown = await response.text();
        const { data: frontmatter, content } = matter(markdown);
        
        // Extract slug from file path
        const slug = filePath.split('/').pop()?.replace('.md', '') || '';
        
        const example: Example = {
          slug,
          title: frontmatter.title || '',
          goal: frontmatter.goal || '',
          tags: frontmatter.tags || [],
          audience: frontmatter.audience || '',
          complexity: frontmatter.complexity || '',
          context: frontmatter.context || '',
          prompt_template: frontmatter.prompt_template || '',
          variables: frontmatter.variables || [],
          steps: frontmatter.steps || [],
          why_it_works: frontmatter.why_it_works || '',
          failure_modes: frontmatter.failure_modes || [],
          citations: frontmatter.citations || [],
          content
        };
        
        console.log('Loaded example:', example.title);
        return example;
      } catch (error) {
        console.error(`Error loading example from ${filePath}:`, error);
        return null;
      }
    });
    
    // Wait for all files to load in parallel
    const results = await Promise.all(loadPromises);
    
    // Filter out failed loads
    const examples = results.filter((example): example is Example => example !== null);
    console.log(`Successfully loaded ${examples.length} examples`);
    
    // Cache the results
    examplesCache = examples;
    examplesLastFetch = now;
    
    return examples;
  } catch (error) {
    console.error('Error loading examples:', error);
    throw error;
  }
}

/**
 * Load a single example by slug
 */
export async function loadExample(slug: string): Promise<Example | null> {
  const examples = await loadExamples();
  return examples.find(example => example.slug === slug) || null;
}

/**
 * Load all pages from content directory
 */
export async function loadAllPages(): Promise<Page[]> {
  try {
    console.log('Loading pages via HTTP fetch...');
    const manifest = await loadManifest();
    const pages: Page[] = [];
    
    for (const filePath of manifest.pages) {
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          console.error(`Failed to fetch ${filePath}:`, response.status);
          continue;
        }
        
        const markdown = await response.text();
        const { data: frontmatter, content } = matter(markdown);
        
        // Extract slug from file path
        const slug = filePath.split('/').pop()?.replace('.md', '') || '';
        
        const page: Page = {
          slug,
          title: frontmatter.title || '',
          description: frontmatter.description || '',
          content,
          lastModified: frontmatter.lastModified || new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
        };
        
        pages.push(page);
        console.log('Loaded page:', page.title);
      } catch (error) {
        console.error(`Error loading page from ${filePath}:`, error);
      }
    }
    
    console.log(`Successfully loaded ${pages.length} pages`);
    return pages;
  } catch (error) {
    console.error('Error loading pages:', error);
    throw error;
  }
}

/**
 * Load a single page by slug
 */
export async function loadPage(slug: string): Promise<Page | null> {
  const pages = await loadAllPages();
  return pages.find(page => page.slug === slug) || null;
}

/**
 * Search across all content
 */
export async function searchContent(query: string, limit: number = 10) {
  if (query.trim().length < 2) return [];
  
  const [posts, examples] = await Promise.all([
    loadNewsPosts(),
    loadExamples()
  ]);
  
  const results = [];
  const lowerQuery = query.toLowerCase();
  
  // Search news posts
  for (const post of posts) {
    if (
      post.title.toLowerCase().includes(lowerQuery) ||
      post.dek.toLowerCase().includes(lowerQuery) ||
      post.topics.some(topic => topic.includes(lowerQuery))
    ) {
      results.push({
        type: 'news' as const,
        slug: post.slug,
        title: post.title,
        excerpt: post.dek,
        url: `/news/${post.slug}`
      });
    }
  }
  
  // Search examples
  for (const example of examples) {
    if (
      example.title.toLowerCase().includes(lowerQuery) ||
      example.context.toLowerCase().includes(lowerQuery) ||
      example.goal.includes(lowerQuery) ||
      example.audience.includes(lowerQuery)
    ) {
      results.push({
        type: 'example' as const,
        slug: example.slug,
        title: example.title,
        excerpt: example.context,
        url: `/examples/${example.slug}`
      });
    }
  }
  
  return results.slice(0, limit);
}